import Quill from "quill";
import { ClassAttributor, Scope } from "parchment";

// Custom LineBreak blot for Shift+Enter soft line breaks
const Embed = Quill.import("blots/embed") as any;
const BlockEmbed = Quill.import("blots/block/embed") as any;

class LineBreak extends Embed {
  static blotName = "linebreak";
  static tagName = "BR";

  static create() {
    return super.create();
  }

  length() {
    return 1;
  }
}

// Register the LineBreak blot
Quill.register(LineBreak, true);

class HorizontalRule extends BlockEmbed {
  static blotName = "hr";
  static tagName = "HR";
}

Quill.register(HorizontalRule, true);

export const IMAGE_STYLE_VALUES = [
  "centered",
  "float-left",
  "float-right",
] as const;

export const IMAGE_STYLE_CLASS_MAP: Record<string, string> = {
  centered: "img-fluid d-block mx-auto image-center",
  "float-left": "img-fluid d-block image-left",
  "float-right": "img-fluid d-block image-right",
};

// Base classes applied to all images
export const IMAGE_BASE_CLASSES = "img-fluid d-block";

const managedImageStyleClasses = new Set(
  Object.values(IMAGE_STYLE_CLASS_MAP)
    .join(" ")
    .split(" ")
    .filter(Boolean)
);

function getImageStyleFromNode(domNode: HTMLImageElement): string | null {
  const explicitStyle = domNode.getAttribute("data-eb-image-style") ?? "";
  if (explicitStyle && IMAGE_STYLE_VALUES.includes(explicitStyle as any)) {
    return explicitStyle;
  }

  const classList = Array.from(domNode.classList);
  for (const [style, classes] of Object.entries(IMAGE_STYLE_CLASS_MAP)) {
    const tokens = classes.split(" ").filter(Boolean);
    const matches = tokens.every((token) => classList.includes(token));
    if (matches) return style;
  }
  return null;
}

function clearManagedImageStyleClasses(domNode: HTMLImageElement) {
  managedImageStyleClasses.forEach((cls) => domNode.classList.remove(cls));
  if (!domNode.className.trim()) {
    domNode.removeAttribute("class");
  }
}

const BaseImage = Quill.import("formats/image") as any;

class CustomImage extends BaseImage {
  static blotName = "image";

  static formats(domNode: HTMLImageElement) {
    const formats = super.formats(domNode) || {};
    const style = getImageStyleFromNode(domNode);
    if (style) {
      formats.imageStyle = style;
    }
    const alt = domNode.getAttribute("alt");
    if (alt !== null) {
      formats.imageAlt = alt;
    }
    const width = domNode.getAttribute("width");
    if (width) {
      formats.imageWidth = width;
    }
    const height = domNode.getAttribute("height");
    if (height) {
      formats.imageHeight = height;
    }
    return formats;
  }

  format(name: string, value: any) {
    if (name === "imageStyle") {
      const node = this.domNode as HTMLImageElement;
      clearManagedImageStyleClasses(node);
      if (typeof value === "string" && value && IMAGE_STYLE_CLASS_MAP[value]) {
        node.setAttribute("data-eb-image-style", value);
        IMAGE_STYLE_CLASS_MAP[value]
          .split(" ")
          .filter(Boolean)
          .forEach((cls) => node.classList.add(cls));
      } else {
        node.removeAttribute("data-eb-image-style");
      }
      return;
    }
    if (name === "imageAlt") {
      const node = this.domNode as HTMLImageElement;
      if (typeof value === "string") {
        node.setAttribute("alt", value);
      } else {
        node.removeAttribute("alt");
      }
      return;
    }
    if (name === "imageWidth") {
      const node = this.domNode as HTMLImageElement;
      if (typeof value === "string" && value.trim()) {
        node.setAttribute("width", value.trim());
      } else {
        node.removeAttribute("width");
      }
      return;
    }
    if (name === "imageHeight") {
      const node = this.domNode as HTMLImageElement;
      if (typeof value === "string" && value.trim()) {
        node.setAttribute("height", value.trim());
      } else {
        node.removeAttribute("height");
      }
      return;
    }
    super.format(name, value);
  }
}

Quill.register(CustomImage, true);

// Custom Link blot that supports target="_blank" for opening in new window
const Inline = Quill.import("blots/inline") as any;

class CustomLink extends Inline {
  static blotName = "link";
  static tagName = "A";
  static SANITIZED_URL = "about:blank";
  static PROTOCOL_WHITELIST = ["http", "https", "mailto", "tel"];

  static create(value: string | { href: string; target?: string }) {
    const node = super.create() as HTMLAnchorElement;

    let href: string;
    let target: string | undefined;

    if (typeof value === "string") {
      href = value;
    } else {
      href = value.href;
      target = value.target;
    }

    // Sanitize the URL
    href = this.sanitize(href);
    node.setAttribute("href", href);
    node.setAttribute("rel", "noopener noreferrer");

    if (target === "_blank") {
      node.setAttribute("target", "_blank");
    }

    return node;
  }

  static sanitize(url: string): string {
    const anchor = document.createElement("a");
    anchor.href = url;
    const protocol = anchor.href.slice(0, anchor.href.indexOf(":"));
    return this.PROTOCOL_WHITELIST.indexOf(protocol) > -1
      ? url
      : this.SANITIZED_URL;
  }

  static formats(domNode: HTMLAnchorElement): { href: string; target?: string } | string {
    const href = domNode.getAttribute("href") || "";
    const target = domNode.getAttribute("target");

    // Return object with both href and target (if present)
    if (target === "_blank") {
      return { href, target: "_blank" };
    }
    // Return just the href string for backward compatibility
    return href;
  }

  format(name: string, value: string | { href: string; target?: string } | boolean) {
    if (name !== this.statics.blotName || !value) {
      super.format(name, value);
    } else {
      let href: string;
      let target: string | undefined;

      if (typeof value === "string") {
        href = value;
      } else if (typeof value === "object") {
        href = value.href;
        target = value.target;
      } else {
        return;
      }

      (this.domNode as HTMLAnchorElement).setAttribute(
        "href",
        CustomLink.sanitize(href)
      );

      if (target === "_blank") {
        (this.domNode as HTMLAnchorElement).setAttribute("target", "_blank");
      } else {
        (this.domNode as HTMLAnchorElement).removeAttribute("target");
      }
    }
  }
}

// Register the custom Link blot (overrides Quill's default link)
Quill.register(CustomLink, true);

// Font size values - empty string means "default/no size class"
export const FONT_SIZE_VALUES = [
  "",
  "14",
  "16",
  "18",
  "20",
  "22",
  "24",
  "26",
  "28",
  "30",
  "32",
  "36",
];

export const FONT_WEIGHT_VALUES = [
  "thin",
  "extralight",
  "light",
  "normal",
  "medium",
  "semibold",
  "bold",
  "extrabold",
  "black",
];

export const TEXT_COLOR_VALUES = [
  // Remove color option (must be first)
  "no-color",
  // Standard colors
  "white",
  "black",
  "primary",
  "secondary",
  "success",
  "info",
  "warning",
  "danger",
  // Primary scale
  ...[100, 200, 300, 400, 500, 600, 700, 800, 900].map((n) => `primary-${n}`),
  // Secondary scale
  ...[100, 200, 300, 400, 500, 600, 700, 800, 900].map((n) => `secondary-${n}`),
];

export const ALIGN_VALUES = ["", "center", "right", "justify"];

export const TOOLBAR_OPTIONS = [
  [{ header: [false, 1, 2, 3, 4, 5, 6] }],
  [{ size: FONT_SIZE_VALUES }],
  ["bold", "italic", "underline", "strike"],
  [{ align: ALIGN_VALUES }],
  [{ color: TEXT_COLOR_VALUES }],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link", "image", "hr"],
  ["clean"],
];

export const ALLOWED_FORMATS = [
  "bold",
  "italic",
  "underline",
  "strike",
  "link",
  "color",
  "size",
  "weight",
  "align",
  "header",
  "list",
  "image",
  "hr",
  "linebreak",
  "table",
  "table-row",
  "table-body",
  "table-container",
];

let quillFormatsRegistered = false;

export function registerBootstrapFormats() {
  if (quillFormatsRegistered) return;
  quillFormatsRegistered = true;

  const SizeClass = new ClassAttributor("size", "fs", {
    scope: Scope.INLINE,
    whitelist: FONT_SIZE_VALUES,
  });

  const WeightClass = new ClassAttributor("weight", "fw", {
    scope: Scope.INLINE,
    whitelist: FONT_WEIGHT_VALUES,
  });

  const ColorClass = new ClassAttributor("color", "text", {
    scope: Scope.INLINE,
    whitelist: TEXT_COLOR_VALUES,
  });

  // Quill's built-in align format already exists, so we don't override it
  // It uses standard values: '', 'center', 'right', 'justify'

  Quill.register(SizeClass, true);
  Quill.register(WeightClass, true);
  Quill.register(ColorClass, true);

  // --- Patch table blots to support proper thead/tbody structure ---

  // Import table blots
  const TableCellBlot = Quill.import("formats/table") as any;
  const TableRowBlot = Quill.import("formats/table-row") as any;
  const TableBodyBlot = Quill.import("formats/table-body") as any;
  const TableContainerBlot = Quill.import("formats/table-container") as any;

  // Patch TableCell to support <th> for header rows
  if (TableCellBlot) {
    TableCellBlot.tagName = ["TD", "TH"];
    TableCellBlot.create = function (value: string) {
      const isHeader = typeof value === "string" && value.startsWith("hdr-");
      const node = document.createElement(isHeader ? "TH" : "TD");
      if (value) {
        node.setAttribute("data-row", value);
      } else {
        const id = Math.random().toString(36).slice(2, 6);
        node.setAttribute("data-row", `row-${id}`);
      }
      return node;
    };
    Quill.register(TableCellBlot, true);
  }

  // Create custom TableHead blot for <thead>
  if (TableBodyBlot) {
    class TableHead extends TableBodyBlot {
      static blotName = "table-head";
      static tagName = "THEAD";
    }

    // Modify TableBody to use <tbody> tag
    TableBodyBlot.tagName = "TBODY";

    Quill.register(TableHead, true);
    Quill.register(TableBodyBlot, true);
  }

  // Patch TableRow to determine if it should be in thead or tbody
  if (TableRowBlot) {
    const originalCreate = TableRowBlot.create;
    TableRowBlot.create = function (value: string) {
      const node = originalCreate.call(this, value);
      // Store whether this is a header row
      const isHeader = typeof value === "string" && value.startsWith("hdr-");
      if (isHeader) {
        node.setAttribute("data-header-row", "true");
      }
      return node;
    };
    Quill.register(TableRowBlot, true);
  }
}

export function parseDelta(raw: string | null | undefined) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

export const FONT_SIZE_SET = new Set(FONT_SIZE_VALUES);
export const FONT_WEIGHT_SET = new Set(FONT_WEIGHT_VALUES);
export const TEXT_COLOR_SET = new Set(TEXT_COLOR_VALUES);
export const ALIGN_SET = new Set(ALIGN_VALUES);

// --- Table style helpers ---
// Row ID format: "row-xxxx" or "hdr-xxxx" optionally followed by ~style1~style2
// e.g. "row-a1b2~bordered~striped", "hdr-c3d4~bordered"

// Border styles are mutually exclusive (only one can be active at a time)
export const BOOTSTRAP_TABLE_BORDER_STYLES: { key: string; label: string; cls: string }[] = [
  { key: "default", label: "Default", cls: "" },
  { key: "bordered", label: "Bordered", cls: "table-bordered" },
  { key: "borderless", label: "Borderless", cls: "table-borderless" },
];

// Other styles can be combined independently
export const BOOTSTRAP_TABLE_OTHER_STYLES: { key: string; label: string; cls: string }[] = [
  { key: "striped", label: "Striped", cls: "table-striped" },
  { key: "hover", label: "Hover", cls: "table-hover" },
  { key: "dark", label: "Dark", cls: "table-dark" },
  { key: "sm", label: "Small", cls: "table-sm" },
];

// Combined array for backward compatibility
export const BOOTSTRAP_TABLE_STYLES: { key: string; label: string; cls: string }[] = [
  ...BOOTSTRAP_TABLE_BORDER_STYLES,
  ...BOOTSTRAP_TABLE_OTHER_STYLES,
];

export function parseTableRowId(rowId: string): {
  base: string;
  isHeader: boolean;
  styles: string[];
} {
  const parts = rowId.split("~");
  const base = parts[0];
  const isHeader = base.startsWith("hdr-");
  const styles = parts.slice(1).filter(Boolean);
  return { base, isHeader, styles };
}

export function buildTableRowId(base: string, styles: string[]): string {
  if (styles.length === 0) return base;
  return [base, ...styles].join("~");
}

/**
 * Post-process HTML from Quill to ensure proper table structure.
 * Wraps rows containing <th> cells in <thead> and remaining rows in <tbody>.
 * Wraps each table in a div.table-responsive.py-4 for Bootstrap responsive tables.
 */
export function postProcessQuillHtml(html: string): string {
  if (!html) return html;

  // Use DOMParser to manipulate table structure (browser environment)
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Strip unwanted classes and attributes from all elements (MS Word, PDF, etc.)
  const allElements = doc.body.querySelectorAll('*');
  allElements.forEach((element) => {
    // Remove deprecated align attribute (HTML5 uses text-* classes instead)
    element.removeAttribute('align');

    // Remove other unwanted attributes
    element.removeAttribute('lang');
    element.removeAttribute('xml:lang');
    element.removeAttribute('dir');

    if (element.classList.length > 0) {
      const classesToRemove: string[] = [];
      element.classList.forEach((className) => {
        // Keep only Bootstrap classes that we explicitly use
        const isAllowedClass =
          /^fs-\d+$/.test(className) ||           // Font size: fs-14, fs-16, etc.
          /^fw-(thin|extralight|light|normal|medium|semibold|bold|extrabold|black)$/.test(className) || // Font weight
          /^text-(white|black|primary|secondary|success|info|warning|danger|primary-\d+|secondary-\d+|no-color)$/.test(className) || // Text color
          /^text-(start|center|end|justify)$/.test(className) || // Text alignment
          /^align-(top|middle|bottom)$/.test(className) || // Vertical alignment for table cells
          /^table/.test(className) ||             // Table classes
          /^img-fluid$/.test(className) ||        // Image classes
          /^image-(center|left|right)$/.test(className) ||
          /^d-block$/.test(className) ||
          /^mx-auto$/.test(className) ||
          /^my-\d+$/.test(className) ||           // Margin classes
          /^float-(start|end)$/.test(className) ||
          /^table-responsive$/.test(className);   // Table wrapper

        if (!isAllowedClass) {
          classesToRemove.push(className);
        }
      });
      classesToRemove.forEach((className) => element.classList.remove(className));

      // Remove class attribute entirely if empty
      if (element.classList.length === 0) {
        element.removeAttribute('class');
      }
    }
  });

  // Find all tables
  const tables = doc.querySelectorAll('table');

  tables.forEach((table) => {
    fixTableStructure(table, doc);

    // Replace <br> with &nbsp; in empty table cells for consistent rendering
    const cells = table.querySelectorAll('td, th');
    cells.forEach((cell) => {
      // Check if cell contains only a <br> tag (empty cell)
      if (cell.childNodes.length === 1 && cell.firstChild?.nodeName === 'BR') {
        cell.innerHTML = '&nbsp;';
      }
    });

    // Move spacing to the responsive wrapper instead of the table element
    table.classList.remove('my-4');

    // Wrap table in div.table-responsive if not already wrapped
    const parent = table.parentElement;
    // Only wrap if parent is not already a table-responsive div
    if (!parent || !parent.classList.contains('table-responsive')) {
      const wrapper = doc.createElement('div');
      wrapper.className = 'table-responsive py-4';
      // Insert wrapper before table and move table into wrapper
      if (parent) {
        parent.insertBefore(wrapper, table);
      }
      wrapper.appendChild(table);
    } else {
      parent.classList.add('py-4');
    }
  });

  // Strip zero-width space characters used as cursor anchors in table cells
  let result = doc.body.innerHTML;
  result = result.replace(/\u200B/g, '');
  return result;
}

/**
 * Fix table structure to have proper thead/tbody elements.
 * Works on both parsed HTML and live DOM elements.
 */
function fixTableStructure(table: HTMLTableElement, doc?: Document) {
  const ownerDoc = doc || table.ownerDocument;

  // Get all rows - check both direct children and within tbody
  let rows = Array.from(table.querySelectorAll(':scope > tr'));

  // Also check for rows inside a single tbody (Quill's default structure)
  const existingTbody = table.querySelector(':scope > tbody');
  if (existingTbody && rows.length === 0) {
    rows = Array.from(existingTbody.querySelectorAll(':scope > tr'));
  }

  if (rows.length === 0) return;

  // Separate header rows (containing th) from body rows (containing td)
  const headerRows: HTMLTableRowElement[] = [];
  const bodyRows: HTMLTableRowElement[] = [];

  rows.forEach((row) => {
    const hasTh = row.querySelector('th') !== null;
    if (hasTh) {
      headerRows.push(row as HTMLTableRowElement);
    } else {
      bodyRows.push(row as HTMLTableRowElement);
    }
  });

  // If we already have proper structure, don't modify
  const existingThead = table.querySelector(':scope > thead');
  if (existingThead && headerRows.length > 0) {
    // Check if all header rows are already in thead
    const theadRows = Array.from(existingThead.querySelectorAll(':scope > tr'));
    if (theadRows.length === headerRows.length) {
      return; // Already properly structured
    }
  }

  // Clear the table
  table.innerHTML = '';

  // Add thead if there are header rows
  if (headerRows.length > 0) {
    const thead = ownerDoc.createElement('thead');
    headerRows.forEach((row) => thead.appendChild(row));
    table.appendChild(thead);
  }

  // Add tbody if there are body rows
  if (bodyRows.length > 0) {
    const tbody = ownerDoc.createElement('tbody');
    bodyRows.forEach((row) => tbody.appendChild(row));
    table.appendChild(tbody);
  }
}

/**
 * Fix table structure in the live editor DOM.
 * Call this after table modifications to ensure proper thead/tbody structure.
 *
 * NOTE: This is currently disabled because Quill's table module doesn't work
 * properly with thead/tbody separation in the editor. We only fix the structure
 * when saving via postProcessQuillHtml.
 */
export function fixEditorTableStructure(editorElement: HTMLElement): void {
  // Disabled - Quill needs all rows in tbody to function properly
  // The structure is fixed only when saving HTML via postProcessQuillHtml
  return;
}
